import React, { useRef } from 'react'
import { Canvas, useFrame } from '@react-three/fiber'
import { Text, RoundedBox } from '@react-three/drei'
import * as THREE from 'three'

interface CreditCard3DProps {
  type: 'visa' | 'mastercard'
  className?: string
}

const Card3D: React.FC<{ type: 'visa' | 'mastercard' }> = ({ type }) => {
  const meshRef = useRef<THREE.Group>(null)
  
  useFrame((state) => {
    if (meshRef.current) {
      meshRef.current.rotation.y = Math.sin(state.clock.elapsedTime * 0.5) * 0.1
      meshRef.current.rotation.x = Math.sin(state.clock.elapsedTime * 0.3) * 0.05
      meshRef.current.position.y = Math.sin(state.clock.elapsedTime * 0.8) * 0.1
    }
  })

  const cardColor = type === 'visa' ? '#1A1F71' : '#EB001B'
  const accentColor = type === 'visa' ? '#FDB913' : '#FF5F00'

  return (
    <group ref={meshRef}>
      {/* Card Base */}
      <RoundedBox
        args={[3.4, 2.1, 0.1]}
        radius={0.1}
        smoothness={4}
        position={[0, 0, 0]}
      >
        <meshStandardMaterial
          color={cardColor}
          metalness={0.8}
          roughness={0.2}
          transparent
          opacity={0.9}
        />
      </RoundedBox>
      
      {/* Card Chip */}
      <RoundedBox
        args={[0.3, 0.25, 0.05]}
        radius={0.02}
        position={[-0.8, 0.3, 0.06]}
      >
        <meshStandardMaterial
          color="#FFD700"
          metalness={1}
          roughness={0.1}
        />
      </RoundedBox>
      
      {/* Card Numbers */}
      <Text
        position={[0, -0.2, 0.06]}
        fontSize={0.15}
        color="white"
        anchorX="center"
        anchorY="middle"
      >
        •••• •••• •••• 1234
      </Text>
      
      {/* Cardholder Name */}
      <Text
        position={[-0.5, -0.6, 0.06]}
        fontSize={0.1}
        color="white"
        anchorX="left"
        anchorY="middle"
      >
        JOHN DOE
      </Text>
      
      {/* Expiry */}
      <Text
        position={[0.8, -0.6, 0.06]}
        fontSize={0.1}
        color="white"
        anchorX="center"
        anchorY="middle"
      >
        12/28
      </Text>
      
      {/* Logo Area */}
      <RoundedBox
        args={[0.6, 0.4, 0.02]}
        radius={0.05}
        position={[1, 0.5, 0.06]}
      >
        <meshStandardMaterial
          color={accentColor}
          transparent
          opacity={0.8}
        />
      </RoundedBox>
      
      {/* Brand Text */}
      <Text
        position={[1, 0.5, 0.08]}
        fontSize={0.12}
        color="white"
        anchorX="center"
        anchorY="middle"
      >
        {type.toUpperCase()}
      </Text>
    </group>
  )
}

const CreditCard3D: React.FC<CreditCard3DProps> = ({ type, className = '' }) => {
  return (
    <div className={`w-64 h-40 ${className}`}>
      <Canvas
        camera={{ position: [0, 0, 5], fov: 50 }}
        style={{ background: 'transparent' }}
      >
        <ambientLight intensity={0.5} />
        <pointLight position={[10, 10, 10]} intensity={1} />
        <pointLight position={[-10, -10, -10]} intensity={0.5} color="#00F5FF" />
        <Card3D type={type} />
      </Canvas>
    </div>
  )
}

export default CreditCard3D
